import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categories } from '@/mocks/articles';

export default function Home() {
  const featuredArticles = useMemo(() => articles.filter((a) => a.featured).slice(0, 6), []);
  const latestArticles = useMemo(() => [...articles].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 8), []);
  const hotArticles = useMemo(() => [...articles].sort((a, b) => b.readCount - a.readCount).slice(0, 5), []);

  return (
    <>
      <SEOHead
        title="淫母日记 - 成人日记文学 | 最新热门日记在线阅读"
        description="淫母日记为您提供最新最全的成人日记文学作品在线阅读。涵盖日记系列、短篇故事、连载小说等多个分类，每日更新高质量成人文学内容，带给您沉浸式阅读体验。"
        keywords="淫母日记,成人日记,日记文学,短篇故事,连载小说,在线阅读"
        canonicalUrl="https://elinggong.cn"
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '淫母日记 - 首页',
          description: '淫母日记首页，提供最新成人日记文学作品在线阅读',
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full h-[520px] md:h-[680px] overflow-hidden">
            <div className="absolute inset-0 w-full h-full">
              <img
                src="https://readdy.ai/api/search-image?query=An%20elegant%20dark%20bedroom%20with%20soft%20ambient%20lighting%2C%20a%20leather%20journal%20on%20silk%20bedsheets%2C%20candle%20glow%2C%20sensual%20and%20intimate%20atmosphere%2C%20dark%20warm%20tones%2C%20cinematic%20composition%2C%20mysterious%20mood%2C%20shallow%20depth%20of%20field&width=1920&height=680&seq=home-hero-2026&orientation=landscape"
                alt="淫母日记 - 成人日记文学阅读平台"
                className="w-full h-full object-cover object-top"
                title="淫母日记 - 成人日记文学"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-background-50/40 via-background-50/25 to-background-50/80"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-50 tracking-wide leading-tight">
                淫母日记
              </h1>
              <p className="mt-4 md:mt-6 text-sm md:text-lg text-foreground-300 max-w-xl leading-relaxed">
                沉浸式成人日记文学阅读平台，探索欲望与人性的深处
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/category/diary-series"
                  className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-label text-sm hover:bg-primary-400 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  开始阅读
                </Link>
                <Link
                  to="/about"
                  className="px-6 py-2.5 rounded-full border border-foreground-300/40 text-foreground-200 font-label text-sm hover:border-foreground-200 hover:text-foreground-50 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  了解更多
                </Link>
              </div>
            </div>
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1">
              <span className="text-xs text-foreground-500 font-label">向下滚动</span>
              <i className="ri-arrow-down-line text-foreground-500 animate-bounce"></i>
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-50">
                  精选推荐
                </h2>
                <p className="mt-2 text-sm text-foreground-500">编辑团队精心挑选的高质量作品</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </section>

          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row gap-10">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-50">
                        最新发布
                      </h2>
                      <p className="mt-1 text-sm text-foreground-500">每日更新，精彩不断</p>
                    </div>
                    <Link
                      to="/category/all"
                      className="text-sm text-primary-400 hover:text-primary-300 font-label flex items-center gap-1 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      查看全部
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                  <div className="flex flex-col gap-5">
                    {latestArticles.map((article) => (
                      <ArticleCard key={article.id} article={article} variant="compact" />
                    ))}
                  </div>
                </div>

                <div className="lg:w-80 flex-shrink-0">
                  <div className="sticky top-24">
                    <div className="mb-6">
                      <h3 className="text-lg font-heading font-semibold text-foreground-100 mb-5">
                        热门排行
                      </h3>
                      <div className="flex flex-col gap-4">
                        {hotArticles.map((article, index) => (
                          <Link
                            key={article.id}
                            to={`/article/${article.id}`}
                            className="flex items-start gap-3 group cursor-pointer"
                          >
                            <span
                              className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded text-xs font-label font-bold ${
                                index === 0
                                  ? 'bg-accent-500 text-background-50'
                                  : index === 1
                                  ? 'bg-accent-500/70 text-background-50'
                                  : index === 2
                                  ? 'bg-accent-500/50 text-background-50'
                                  : 'bg-background-200 text-foreground-500'
                              }`}
                            >
                              {index + 1}
                            </span>
                            <div className="min-w-0">
                              <h4 className="text-sm text-foreground-300 line-clamp-2 leading-snug group-hover:text-foreground-100 transition-colors">
                                {article.title}
                              </h4>
                              <p className="mt-1 text-xs text-foreground-600">
                                {article.readCount > 10000
                                  ? `${(article.readCount / 10000).toFixed(1)}万 阅读`
                                  : `${article.readCount} 阅读`}
                              </p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-heading font-semibold text-foreground-100 mb-5">
                        内容分类
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {categories
                          .filter((c) => c.slug !== 'all')
                          .map((cat) => (
                            <Link
                              key={cat.slug}
                              to={`/category/${cat.slug}`}
                              className="px-3.5 py-1.5 rounded-full text-xs font-label bg-background-200 text-foreground-400 hover:bg-primary-500/20 hover:text-primary-400 transition-all duration-300 cursor-pointer whitespace-nowrap"
                            >
                              {cat.name}
                            </Link>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-50">
                读者常见问题
              </h2>
              <p className="mt-2 text-sm text-foreground-500">关于淫母日记的常见问题解答</p>
            </div>

            <SchemaOrg
              type="FAQPage"
              data={{
                mainEntity: [
                  {
                    '@type': 'Question',
                    name: '淫母日记是什么类型的网站？',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: '淫母日记是一个专注于成人日记文学创作的在线阅读平台，提供日记系列、短篇故事、连载小说等多种类型的高质量原创文学作品。所有内容仅供18岁以上成年读者阅读。',
                    },
                  },
                  {
                    '@type': 'Question',
                    name: '如何阅读淫母日记的文章？',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: '您可以直接在网站上免费浏览所有文章。通过首页导航或分类页面，找到感兴趣的作品，点击即可进入阅读页面。无需注册或登录即可阅读全部内容。',
                    },
                  },
                  {
                    '@type': 'Question',
                    name: '淫母日记的内容多久更新一次？',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: '我们保持每日更新，每天都会有新的原创日记文学作品发布。您可以关注首页的"最新发布"栏目，获取每日更新的最新内容。',
                    },
                  },
                  {
                    '@type': 'Question',
                    name: '如何成为淫母日记的作者？',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: '如果您有优秀的成人日记文学作品想要发布，可以通过"关于我们"页面提供的联系方式与我们取得联系。我们的编辑团队会认真审阅每一份投稿。',
                    },
                  },
                ],
              }}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
              {[
                {
                  q: '淫母日记是什么类型的网站？',
                  a: '淫母日记是一个专注于成人日记文学创作的在线阅读平台，提供日记系列、短篇故事、连载小说等多种类型的高质量原创文学作品。所有内容仅供18岁以上成年读者阅读。',
                },
                {
                  q: '如何阅读淫母日记的文章？',
                  a: '您可以直接在网站上免费浏览所有文章。通过首页导航或分类页面，找到感兴趣的作品，点击即可进入阅读页面。无需注册或登录即可阅读全部内容。',
                },
                {
                  q: '淫母日记的内容多久更新一次？',
                  a: '我们保持每日更新，每天都会有新的原创日记文学作品发布。您可以关注首页的最新发布栏目，获取每日更新的最新内容。',
                },
                {
                  q: '如何成为淫母日记的作者？',
                  a: '如果您有优秀的成人日记文学作品想要发布，可以通过关于我们页面提供的联系方式与我们取得联系。我们的编辑团队会认真审阅每一份投稿。',
                },
              ].map((faq, index) => (
                <div key={index} className="bg-background-100 rounded-lg p-5">
                  <h4 className="text-sm font-label font-medium text-foreground-200 mb-2">
                    {faq.q}
                  </h4>
                  <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-accent-500/10 py-14 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-50">
                开启阅读之旅
              </h2>
              <p className="mt-3 text-sm text-foreground-400 max-w-lg mx-auto leading-relaxed">
                这里有最动人的日记文学，最真实的情感表达。无论你是想寻找共鸣，还是探索未知的世界，淫母日记都不会让你失望。
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/category/all"
                  className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-label text-sm hover:bg-primary-400 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  浏览全部文章
                </Link>
                <Link
                  to="/about"
                  className="px-6 py-2.5 rounded-full border border-foreground-300/40 text-foreground-200 font-label text-sm hover:border-foreground-200 hover:text-foreground-50 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  关于我们
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}